import {
  type DecorationWithType,
  type Editor,
  NodeView,
  type NodeViewRenderer,
  type NodeViewRendererOptions,
  type NodeViewRendererProps,
} from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Decoration, NodeView as ProseMirrorNodeView } from "@tiptap/pm/view";
import type { Component, ValidComponent, Setter } from "solid-js";
import { createRoot } from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic, insert } from "solid-js/web";
import { getTipTapSolidReactiveOwner } from "./ReactiveOwner";
import { SolidNodeViewContext, type SolidNodeViewContextProps } from "./useSolidNodeView";
import { isNil } from "lodash-es";

export type SolidNodeViewRendererOptions = {
  update: ((node: ProseMirrorNode, decorations: Decoration[]) => boolean) | null;
} & NodeViewRendererOptions;

export class SolidNodeView extends NodeView<Component, Editor, SolidNodeViewRendererOptions> {
  rootElement!: HTMLElement | null;
  contentElement!: HTMLElement | null;
  setProps!: Setter<Record<string, unknown>>;
  dispose!: () => void;

  constructor(component: Component, props: NodeViewRendererProps, options?: Partial<SolidNodeViewRendererOptions>) {
    super(component, props, options);
    createRoot((dispose) => {
      this.dispose = dispose;
      const [props, setProps] = createStore({
        editor: this.editor,
        node: this.node,
        decorations: this.decorations,
        selected: false,
        extension: this.extension,
        getPos: () => this.getPos(),
        updateAttributes: (attributes = {}) => this.updateAttributes(attributes),
        deleteNode: () => this.deleteNode(),
      });
      this.setProps = setProps;

      const tagName = this.node.isInline ? "span" : "div";

      this.rootElement = document.createElement(tagName);
      this.rootElement.dataset.nodeViewRenderer = "";
      this.rootElement.classList.add("solid-renderer");

      this.contentElement = this.node.isLeaf ? null : document.createElement(tagName);

      if (!isNil(this.contentElement)) {
        // For some reason the whiteSpace prop is not inherited properly in Chrome and Safari
        // With this fix it seems to work fine
        // See: https://github.com/ueberdosis/tiptap/issues/1197
        this.contentElement.style.whiteSpace = "inherit";
      }

      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const SolidNodeViewProvider: Component<Record<string, unknown>> = (componentProps) => {
        const onDragStart = this.onDragStart.bind(this);
        const nodeViewContentRef: SolidNodeViewContextProps["nodeViewContentRef"] = (element) => {
          if (!isNil(element) && !isNil(this.contentElement) && element.firstChild !== this.contentElement) {
            element.appendChild(this.contentElement);
          }
        };

        return (
          <SolidNodeViewContext.Provider value={{ onDragStart, nodeViewContentRef }}>
            <Dynamic component={this.component} {...componentProps} />
          </SolidNodeViewContext.Provider>
        );
      };

      insert(this.rootElement, SolidNodeViewProvider(props));
    }, getTipTapSolidReactiveOwner(this.editor));
  }

  get dom() {
    if (isNil(this.rootElement?.firstElementChild?.hasAttribute("data-node-view-wrapper"))) {
      throw Error("Please use the NodeViewWrapper component for your node view.");
    }

    return this.rootElement;
  }

  get contentDOM() {
    if (this.node.isLeaf) {
      return null;
    }
    return this.contentElement;
  }

  update(node: ProseMirrorNode, decorations: DecorationWithType[]) {
    if (typeof this.options.update === "function") {
      const oldNode = this.node;
      const oldDecorations = this.decorations;

      this.node = node;
      this.decorations = decorations;

      return this.options.update(oldNode, oldDecorations as Decoration[]);
    }

    if (node.type !== this.node.type) {
      return false;
    }

    if (node === this.node && this.decorations === decorations) {
      return true;
    }

    this.node = node;
    this.decorations = decorations;

    this.setProps({ node, decorations });

    return true;
  }

  selectNode() {
    this.setProps({
      selected: true,
    });
  }

  deselectNode() {
    this.setProps({
      selected: false,
    });
  }

  destroy() {
    if (!isNil(this.rootElement)) {
      this.rootElement.textContent = "";
    }
    this.dispose();
    this.contentElement = null;
    this.rootElement = null;
  }
}

export const SolidNodeViewRenderer =
  (component: ValidComponent, options?: Partial<SolidNodeViewRendererOptions>): NodeViewRenderer =>
  (props: NodeViewRendererProps) =>
    new SolidNodeView(component as Component, props, options) as unknown as ProseMirrorNodeView;
